/**
 * Preloader Animation using GSAP
 * 
 * Animation sequence:
 * 1. Full-screen overlay with centered video/logo
 * 2. After configurable delay → brand text animates in
 * 3. After text is visible → entire logo scales down and moves to header position
 * 4. Background overlay slides up and disappears, revealing the page
 */

document.addEventListener('DOMContentLoaded', function () {
    // Wait for GSAP to be available
    if (typeof gsap === 'undefined') {
        console.warn('GSAP not loaded, skipping preloader animation');
        const preloader = document.getElementById('preloader');
        if (preloader) preloader.style.display = 'none';
        return;
    }

    const preloader = document.getElementById('preloader');
    if (!preloader) return;

    // Lock body scroll
    document.body.classList.add('preloader-active');

    // Elements
    const overlay = preloader.querySelector('.preloader__overlay');
    const content = document.getElementById('preloader-content');
    const logoWrapper = document.getElementById('preloader-logo-wrapper');
    const videoContainer = document.getElementById('preloader-video');
    const textContainer = document.getElementById('preloader-text');
    const brandLines = preloader.querySelectorAll('.preloader__brand-line');
    const textImage = document.getElementById('preloader-text-img');
    const videoEl = document.getElementById('preloader-video-el');

    // Get timing from section settings (passed via data attributes or defaults)
    const textDelay = parseFloat(preloader.closest('.preloader-section')?.dataset?.textDelay) || 1;
    const moveDelay = parseFloat(preloader.closest('.preloader-section')?.dataset?.moveDelay) || 1;

    // ========================================
    // PHASE 1: Initial state - video visible, centered
    // ========================================

    // Play video once (no autoplay, no loop)
    if (videoEl) {
        videoEl.play();
    }

    gsap.set(textContainer, { opacity: 0, visibility: 'hidden' });
    gsap.set(brandLines, { opacity: 0, y: 15 });
    if (textImage) {
        gsap.set(textImage, { opacity: 0, x: -20 });
    }

    // Master timeline
    const tl = gsap.timeline({
        onComplete: function () {
            // Cleanup after animation completes
            preloader.classList.add('preloader--hidden');
            document.body.classList.remove('preloader-active');

            // Remove preloader from DOM after transition
            setTimeout(function () {
                preloader.remove();
            }, 600);
        }
    });

    // ========================================
    // PHASE 2: After delay, animate text in
    // ========================================
    tl.to(textContainer, {
        opacity: 1,
        visibility: 'visible',
        duration: 0.3,
        delay: textDelay,
        ease: 'power2.out'
    });

    // Animate each brand line or text image
    if (brandLines.length > 0) {
        tl.to(brandLines, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.15,
            ease: 'power3.out'
        }, '-=0.1');
    }

    if (textImage) {
        tl.to(textImage, {
            opacity: 1,
            x: 0,
            duration: 0.8,
            ease: 'power3.out'
        }, '-=0.3');
    }

    // ========================================
    // PHASE 3: Move logo to header position
    // ========================================

    // Store computed values for the animation
    let moveData = {};

    // Calculate positions right before the move starts
    tl.call(function () {
        const headerLogo = document.querySelector('.header-logo, .header-logo__image-container, [data-testid="header-logo"]');
        const headerComponent = document.querySelector('header-component, #header-component');

        const targetEl = headerLogo || headerComponent;
        if (!targetEl) {
            moveData.fallback = true;
            return;
        }

        const targetRect = targetEl.getBoundingClientRect();
        const wrapperRect = logoWrapper.getBoundingClientRect();

        // Calculate scale - target roughly the header logo size
        const targetHeight = targetRect.height || 60;
        const currentHeight = wrapperRect.height;
        moveData.scale = Math.min(targetHeight / currentHeight, 0.35);

        // Calculate translation to move to header position
        const targetCenterX = targetRect.left + (targetRect.width / 2);
        const targetCenterY = targetRect.top + (targetRect.height / 2);
        const currentCenterX = wrapperRect.left + (wrapperRect.width / 2);
        const currentCenterY = wrapperRect.top + (wrapperRect.height / 2);

        moveData.deltaX = targetCenterX - currentCenterX;
        moveData.deltaY = targetCenterY - currentCenterY;
    });

    // Animate logo to header position
    tl.to(logoWrapper, {
        duration: 1,
        delay: moveDelay,
        ease: 'power3.inOut',
        onStart: function () {
            if (moveData.fallback) {
                // Fallback: just scale down and move up
                gsap.to(this.targets()[0], { scale: 0.5, y: -200, opacity: 0, duration: 0.8, ease: 'power3.inOut' });
                return;
            }
            gsap.to(this.targets()[0], {
                x: moveData.deltaX,
                y: moveData.deltaY,
                scale: moveData.scale,
                duration: 1,
                ease: 'power3.inOut'
            });
        }
    });

    // Slide overlay up
    tl.to(overlay, {
        y: '-100%',
        duration: 0.8,
        ease: 'power2.inOut'
    }, '-=0.5');

    // Fade out the preloader content as overlay slides
    tl.to(logoWrapper, {
        opacity: 0,
        duration: 0.4,
        ease: 'power2.out'
    }, '-=0.4');

    // After overlay slides, fully hide preloader
    tl.set(preloader, { display: 'none' });
    tl.call(function () {
        document.body.classList.remove('preloader-active');
        preloader.remove();
    });
});

/**
 * Fallback fade-out if header logo is not found
 */
function fadeOutPreloader(tl, preloader, overlay, logoWrapper) {
    tl.to(logoWrapper, {
        scale: 0.5,
        y: -200,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.inOut'
    });

    tl.to(overlay, {
        y: '-100%',
        duration: 0.6,
        ease: 'power2.inOut'
    }, '-=0.4');

    tl.set(preloader, { display: 'none' });
}
